import { CAlert, CCol, CFormInput, CLoadingButton, CRow } from '@coreui/react-pro'

import React, { useState } from 'react'
import Tesseract from 'tesseract.js'
import ToastComponent from 'src/components/common/TaostComponent'
import ScorecardService from 'src/service/ScorecardService'
import { useParams } from 'react-router-dom'

const ImportFixtureScorecard = (props) => {
  const { fixtureId, teamId } = useParams()

  const [files, setFiles] = useState({
    batting: null,
    bowling: null,
    fielding: null,
    dismissals: null,
  })

  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState([])

  /* ---------------- FILE HANDLER ---------------- */
  const handleFileChange = (type, e) => {
    setFiles((prev) => ({
      ...prev,
      [type]: e.target.files[0],
    }))
  }

  /* ---------------- OCR + IMPORT ---------------- */
  const cleanPlayerTags = (line) => {
    line = line.replace(/\bn\b/g, '11')

    return (
      line
        // remove captain / vc / wk ONLY inside brackets
        .replace(/\(\s*(c|vc|ve|v|wk|capt|captain)\s*\)/gi, '')
        // normalize spacing
        .replace(/\s+/g, ' ')
        .trim()
    )
  }

  const normalizeFieldingRow = (line, type) => {
    if (type !== 'fielding') return line

    // Replace © with 0
    line = line.replace(/©/g, '0')

    // Split tokens
    const parts = line.split(' ')
    const nameParts = []
    const nums = []

    for (const p of parts) {
      if (/^\d+$/.test(p)) {
        nums.push(p)
      } else {
        nameParts.push(p)
      }
    }

    // 🔥 Fix merged "01"
    if (nums.length === 4 && nums[0] === '01') {
      nums.splice(0, 1, '0', '1')
    }

    // Ensure exactly 5 numeric columns
    while (nums.length < 5) {
      nums.push('0')
    }

    return [...nameParts, ...nums].join(' ')
  }

  const fixBowlingLine = (line) => {
    const parts = line.trim().split(/\s+/)

    if (parts.length < 5) return line

    // Extract last 4 supposed numeric values
    const nameParts = parts.slice(0, -4)
    let o = parts[parts.length - 4]
    let m = parts[parts.length - 3]
    let r = parts[parts.length - 2]
    let w = parts[parts.length - 1]

    // 🔥 Detect merged "31" when wickets already = 1
    if (/^\d{2}$/.test(r) && /^\d$/.test(w)) {
      // If runs is 2-digit but wickets is 1-digit
      // and original screenshot expected small runs
      // Split if needed
      const first = r[0]
      const second = r[1]

      // If second digit equals wicket, assume OCR merge
      if (second === w) {
        r = first
      }
    }

    return [...nameParts, o, m, r, w].join(' ')
  }

  const extractAndImport = async () => {
    const selectedFiles = Object.entries(files).filter(([, f]) => f)

    if (selectedFiles.length === 0) {
      ToastComponent('Please upload at least one image', 'error')
      return
    }

    setIsLoading(true)
    setErrors([])

    try {
      const payload = []

      for (const [type, file] of selectedFiles) {
        const result = await Tesseract.recognize(file, 'eng')

        let rows = (result.data.text || '')
          .replace(/\r\n|\r/g, '\n')
          .replace(/[ \t]+/g, ' ')
          .split('\n')
          .map((r) => cleanPlayerTags(r.trim()))
          .map((r) => normalizeFieldingRow(r, type)) // 🔥 ADD THIS
          .filter(Boolean)
        if (type === 'bowling') {
          rows = rows.map(fixBowlingLine)
        }
        if (rows.length > 0) {
          payload.push({ type, rows })
        }
      }

      if (payload.length === 0) {
        setErrors(['No readable scorecard data found'])
        setIsLoading(false)
        return
      }

      const res = await ScorecardService.importScorecardFromImage({
        fixtureId,
        teamId,
        payload,
      })

      if (res.success) {
        ToastComponent(res.message || 'Scorecard imported successfully', 'success')
        props.fetchData(props.defaultInning)
      } else {
        setErrors(res.errors || ['Import failed'])
      }
    } catch (err) {
      console.error(err)
      setErrors(['Failed to read image'])
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {errors.map((e, i) => (
        <CAlert key={i} color="danger">
          {e}
        </CAlert>
      ))}

      <CRow className="mb-3">
        <CCol md={4}>
          <label className="form-label">Batters</label>
          <CFormInput
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange('batting', e)}
          />
        </CCol>

        <CCol md={4}>
          <label className="form-label">Bowlers</label>
          <CFormInput
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange('bowling', e)}
          />
        </CCol>

        <CCol md={4}>
          <label className="form-label">Fielders</label>
          <CFormInput
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange('fielding', e)}
          />
        </CCol>
        <CCol md={4} className="pt-3">
          <label className="form-label">Dismissals</label>
          <CFormInput
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange('dismissals', e)}
          />
        </CCol>
      </CRow>

      <CRow>
        <CCol md={12} className="text-end">
          <CLoadingButton
            color="success"
            variant="outline"
            loading={isLoading}
            onClick={extractAndImport}
          >
            Extract & Add
          </CLoadingButton>
        </CCol>
      </CRow>
    </>
  )
}

export default ImportFixtureScorecard
